/**
 * translatedPhraseEditing.ts
 *
 * Pure helpers shared by every surface that hosts the right-click "Edit
 * translation" popover — the inline prose views (`TranscriptionTextTranslated`,
 * `TranscriptionDiffTranslated`) and the Translation Review workspace
 * (`AlignedBlocksView`, PLAT-10264). They resolve the two read-only strings the
 * editor popover shows alongside the editable translation: the source-language
 * text and the machine-original translation. Extracted here (were duplicated,
 * module-private, in both prose components) so the workspace reuses the IDENTICAL
 * logic rather than deep-importing one feature component from another.
 *
 * No React, no store — a deterministic projection over a phrase + the source
 * element array.
 */

import { joinFragments } from '@src/stores-v2/translationSpanEdit';
import { joinPhraseValues, normalizeForCompare } from '@src/utility/strings';
import { normalizeWordBoundaries } from '@src/utility/videoTranscriptionUtils';

import type { TranscriptElement } from '@src/types.ts/transcript';
import type { TranslationPhrase } from '@src/types.ts/translation';

/**
 * The original-language text behind a translated phrase, for the popover's
 * read-only "source" line. When the live source elements are available it
 * reconstructs a punctuation-complete string across the phrase's covered source
 * span (so interior punctuation — a period between two covered words — is kept);
 * it falls back to the phrase's captured snapshot only when no elements are
 * available. Reconstructing wins over the stored snapshot because the snapshot
 * omits interior punctuation (e.g. on a re-opened override), which would
 * otherwise render "sentence 1 sentence 2" instead of "sentence 1. sentence 2".
 *
 * The covered elements are matched by the phrase's stable element UUIDs
 * (`originalElementIds`, matched against each element's `source_uuid ?? id`).
 * This is what lets reused clip elements — whose `initial_index` is renumbered
 * (often to -1) while `source_uuid` survives — still resolve their source text.
 * When the phrase carries no ids or none resolve, it falls back to the phrase's
 * captured `originalValueSnapshot`.
 */
export function resolveSourceText(
  phrase: TranslationPhrase,
  baseElements: TranscriptElement[],
): string {
  // PLAT-10611 — collect the covered elements by the phrase's stable element
  // UUIDs (`source_uuid ?? id`). A reused clip element keeps its story-space
  // `source_uuid` but its `initial_index` is renumbered (often to -1), so a
  // positional walk would resolve to the empty snapshot on reused clips. Walk in
  // document order and join every element whose UUID is in the phrase's id set,
  // taking the FIRST occurrence per UUID: a montage move duplicates a source
  // word in `baseElements` (the struck ghost at its old position + the kept
  // re-add at the new one, both sharing one `source_uuid`), which would
  // otherwise render the source text twice.
  if (
    baseElements.length > 0 &&
    phrase.originalElementIds &&
    phrase.originalElementIds.length > 0
  ) {
    const idSet = new Set(phrase.originalElementIds);
    const seen = new Set<string>();
    const parts: string[] = [];
    for (const el of baseElements) {
      const uuid = el.source_uuid ?? el.id;
      if (uuid === undefined || !idSet.has(uuid) || seen.has(uuid)) continue;
      if (
        (el.type === 'text' || el.type === 'punct') &&
        el.value &&
        el.value.trim().length > 0
      ) {
        seen.add(uuid);
        parts.push(el.value);
      }
    }
    if (parts.length > 0) return joinPhraseValues(parts);
  }
  return phrase.originalValueSnapshot ?? '';
}

/**
 * Map a covered run of translated phrases to the `transcriptElements`
 * ARRAY-POSITION range the transcript action handlers consume (the array-index
 * space the original view encodes as `data-index`, NOT `initial_index`), for
 * the translated context-menu actions (PLAT-10646).
 *
 * Keyed on stable element UUIDs only (`originalElementIds`, matched against each
 * element's `source_uuid ?? id`) — the durable key that survives the editor
 * renumbering `initial_index` on every edit and reused-clip elements (whose
 * `initial_index` is renumbered, often to -1, while `source_uuid` survives).
 *
 * KEPT-aware: it collects only the array positions of covered elements that are
 * still KEPT (state not `removed`/`cut`), then returns their `[min, max]` span
 * snapped onto real words via `normalizeWordBoundaries`. Filtering to kept
 * occurrences is what makes a montage MOVE correct — a moved word appears twice
 * (a struck ghost at its old array position + a kept re-add at its new one,
 * both sharing one `source_uuid`); spanning min..max over BOTH would engulf
 * everything between, so only the playing copy is considered.
 *
 * Returns null when no kept covered element survives (the whole run was
 * cut/removed) or the phrases carry no resolvable ids. Callers must not dispatch
 * a source-range action without a range; the menu falls back to Fix Translation
 * only.
 */
export function mapCoveredSourceToElementRange(
  elements: TranscriptElement[],
  coveredPhrases: readonly TranslationPhrase[],
): { startIndex: number; endIndex: number } | null {
  if (elements.length === 0) return null;

  const idSet = new Set<string>();
  for (const phrase of coveredPhrases) {
    for (const id of phrase.originalElementIds ?? []) idSet.add(id);
  }
  if (idSet.size === 0) return null;

  let start = -1;
  let end = -1;
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    if (el.state === 'removed' || el.state === 'cut') continue;
    const uuid = el.source_uuid ?? el.id;
    if (uuid === undefined || !idSet.has(uuid)) continue;
    if (start === -1) start = i;
    end = i;
  }
  if (start === -1) return null;
  return normalizeWordBoundaries(start, end, elements);
}

/**
 * The machine-original translation to revert to (popover "Revert" target /
 * baseline). Prefers the explicitly captured pre-edit value; for a phrase that
 * was never user-edited the current value IS the original; otherwise — for an
 * override fragment (`parentId` set) — the machine baseline is the text of the
 * fragments this override retired, joined from the supplied phrase list (the
 * same baseline the span-replace editor shows; see useSpanReplace). When the
 * phrase list is omitted, or the override retired no fragments, it stays
 * unknown.
 */
export function resolveOriginalValue(
  phrase: TranslationPhrase,
  phrases?: readonly TranslationPhrase[],
): string | undefined {
  // A refetched translation returns absent optional fields as `null`, not
  // `undefined` (the backend normalizes on write). Coalesce both to `undefined`
  // so an override fragment — whose `originalTranslatedValue` comes back `null`
  // after the confirm→refetch — falls through to the retired-originals baseline
  // below instead of short-circuiting here with `null`. Without this the
  // changed-word comparison never runs and the whole segment reads edited
  // (PLAT-10270).
  const captured = phrase.originalTranslatedValue ?? undefined;
  if (captured !== undefined) {
    return captured;
  }
  if (!phrase.isUserEdited) return phrase.translatedValue;
  if (phrase.parentId !== undefined && phrases) {
    // Scope the machine baseline to the retired originals that OVERLAP this
    // fragment's own source indices — not every fragment the override retired.
    // After confirm the whole-segment override is fine-split into per-word
    // fragments; joining ALL retired originals would compare each single word
    // against the whole sentence, so every word reads edited and the segment
    // turns green (PLAT-10270). A single (pre-split) fragment still overlaps
    // every retired original, so its baseline is unchanged.
    const own = new Set(phrase.originalIndices);
    return (
      joinFragments(
        phrases.filter(
          p =>
            p.supersededBy === phrase.parentId &&
            p.originalIndices.some(i => own.has(i)),
        ),
      ) || undefined
    );
  }
  return undefined;
}

/**
 * Whether a phrase's APPLIED translation differs from its machine baseline —
 * the single "edited" predicate behind the changed-phrase visual (green mark +
 * hover tooltip). Reuses `resolveOriginalValue`, so it covers both storage
 * shapes: a phrase-record edit and a span-replace override fragment. When the
 * baseline can't be resolved (a legacy edit with no captured original, or an
 * override fragment with the phrase list omitted) it falls back to the
 * `isUserEdited` flag, so a known edit still reads as edited.
 */
export function isEdited(
  phrase: TranslationPhrase,
  phrases?: readonly TranslationPhrase[],
): boolean {
  const base = resolveOriginalValue(phrase, phrases);
  if (base === undefined) return phrase.isUserEdited === true;
  // Whitespace-insensitive on BOTH operands: a phantom space before punctuation
  // must not read as edited, and this also removes the prior asymmetry where
  // `base` (a joined value) was effectively normalized but `translatedValue` raw.
  return (
    normalizeForCompare(base) !== normalizeForCompare(phrase.translatedValue)
  );
}

/**
 * Whether a phrase's translation was REMOVED by the reviewer — the "Remove
 * Translation" action's storage shape (PLAT-11340 follow-up).
 *
 * There is no removal flag: a removal is an ordinary correction whose applied
 * value is the empty string, so it inherits the whole edit machinery for free
 * (revert, the changelog entry, the changed-phrase mark, the dub's
 * out-of-date check, and `srtExport`'s existing `if (!value) continue` skip).
 * This predicate is what tells the two apart.
 *
 * Emptiness alone is NOT enough: the machine translation legitimately emits
 * whitespace-only phrases (the inter-phrase spacing carriers the prose views
 * class as `whitespace`), and marking those removed would strike text nobody
 * touched. So it is emptiness AND `isEdited` — the same predicate behind the
 * green changed mark — which holds for both storage shapes (a phrase-record
 * edit captures `originalTranslatedValue`; an override fragment resolves its
 * baseline from the fragments it retired).
 */
export function isTranslationRemoved(
  phrase: TranslationPhrase,
  phrases?: readonly TranslationPhrase[],
): boolean {
  return (
    (phrase.translatedValue ?? '').trim() === '' && isEdited(phrase, phrases)
  );
}
